import React, { useEffect, useRef, useState } from 'react';
import { useInView, useReducedMotion, animate } from 'framer-motion';

/**
 * Animated number that counts from 0 → `to` when scrolled into view.
 * Uses tabular figures (via .tabular-nums) so width doesn't jitter mid-count.
 * Announces the final value to screen readers; under reduced-motion it shows
 * the final value instantly.
 *
 * @param {object} props
 * @param {number} props.to            target value
 * @param {string} [props.prefix='']
 * @param {string} [props.suffix='']
 * @param {number} [props.duration=1.6]
 * @param {boolean}[props.compact=false] format large numbers locale-aware (e.g. 12,480)
 */
export function CountUp({ to, prefix = '', suffix = '', duration = 1.6, compact = true, className }) {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: '-60px' });
    const reduce = useReducedMotion();
    const [value, setValue] = useState(0);

    useEffect(() => {
        if (!inView) return;
        if (reduce) { setValue(to); return; }
        const controls = animate(0, to, {
            duration,
            ease: [0.22, 1, 0.36, 1],
            onUpdate: (v) => setValue(v),
        });
        return () => controls.stop();
    }, [inView, reduce, to, duration]);

    const display = compact ? Math.round(value).toLocaleString() : Math.round(value);

    return (
        <span
            ref={ref}
            className={`tabular-nums ${className || ''}`}
            aria-live="polite"
            aria-label={`${prefix}${to.toLocaleString()}${suffix}`}
        >
            {prefix}{display}{suffix}
        </span>
    );
}

export default CountUp;
